'use client';
import { useCallback } from 'react';

import { useIncrementAdvertisementView } from '@/components/shared/services/hooks';

const SEEN_ADVERTISEMENTS_STORAGE_KEY = '_zhk_seen_ads';

/**
 * Ids already counted in this tab. Kept at module scope so the dedupe survives client-side
 * navigation (the store is shared by every hook consumer), and mirrored into `sessionStorage`
 * so it also survives a full reload for as long as the tab is open.
 */
const seenAdvertisementIds = new Set<string>();
let isRestored = false;

function restoreSeenAdvertisementIds() {
  if (isRestored) {
    return;
  }
  isRestored = true;
  try {
    const stored = window.sessionStorage.getItem(SEEN_ADVERTISEMENTS_STORAGE_KEY);
    if (stored) {
      (JSON.parse(stored) as string[]).forEach((id) => seenAdvertisementIds.add(id));
    }
  } catch {
    // sessionStorage can be unavailable (private mode / blocked storage) — dedupe then only
    // lives in memory for this page, which is still enough for menu browsing.
  }
}

function persistSeenAdvertisementIds() {
  try {
    window.sessionStorage.setItem(
      SEEN_ADVERTISEMENTS_STORAGE_KEY,
      JSON.stringify(Array.from(seenAdvertisementIds)),
    );
  } catch {
    // see restoreSeenAdvertisementIds
  }
}

/**
 * Counts one advertisement impression per advertisement id per tab session.
 *
 * Call `trackView(id)` only once the ad is actually on screen for the user (e.g. from the
 * image's `onLoad` while its container is visible). If two rendered ad slots share the same
 * backend id (e.g. the menu banners — see the menu-ads doc), only the first one to call
 * `trackView` counts, by design: it's one advertisement record, so it should be one impression.
 */
export default function useAdvertisementView() {
  const { mutate: incrementAdvertisementView } = useIncrementAdvertisementView();

  const trackView = useCallback(
    (id?: string) => {
      if (!id) {
        return;
      }
      restoreSeenAdvertisementIds();
      if (seenAdvertisementIds.has(id)) {
        return;
      }
      // Marked as seen *before* firing: impressions are fire-and-forget, and over-counting a
      // re-render is worse than losing one impression to a failed request.
      seenAdvertisementIds.add(id);
      persistSeenAdvertisementIds();
      incrementAdvertisementView(id);
    },
    [incrementAdvertisementView],
  );

  return { trackView };
}
